/**
 * Shared time-grid utilities used across Atelier, Reception, and Disponibilités screens.
 */

/** Snap a decimal hour to the nearest grid increment */
export function snapToGrid(hour: number, snapUnit: number): number {
  return Math.round(hour / snapUnit) * snapUnit;
}

/** Visible planning grid bounds (hours). A full-day block spans this range. */
export const GRID_HOUR_START = 7;
export const GRID_HOUR_END = 20;

/** Format a Date as a local ISO date (YYYY-MM-DD). */
export function isoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Epoch seconds (noon, to dodge DST) for an ISO date — for the DatePicker component. */
export function isoToSeconds(iso: string): number {
  return Math.floor(new Date(iso + "T12:00:00").getTime() / 1000);
}

/** ISO date (YYYY-MM-DD) from epoch seconds. */
export function secondsToIso(seconds: number): string {
  return isoDate(new Date(seconds * 1000));
}

/** Whole calendar days from aIso to bIso (b − a). Noon-anchored to avoid DST drift. */
export function daysBetween(aIso: string, bIso: string): number {
  const a = new Date(aIso + "T12:00:00").getTime();
  const b = new Date(bIso + "T12:00:00").getTime();
  return Math.round((b - a) / 86400000);
}

/**
 * Whole-minute values of the 7.5-min grid, floored: floor(k * 7.5) for k = 0..7.
 * A reception/restitution time is "on grid" when its minute is one of these.
 */
export const ON_GRID_MINUTES = [0, 7, 15, 22, 30, 37, 45, 52];

/**
 * True when an epoch-seconds timestamp's local time is NOT aligned to the
 * 7.5-min planning grid (e.g. 07:38, a legacy round-up of the 07:37 slot). Used
 * to detect RDV stored off-grid (legacy rounding, imports, ServiceBox sync…).
 */
export function isTimeOffGrid(epochSeconds?: number | null): boolean {
  if (!epochSeconds) return false;
  const d = new Date(epochSeconds * 1000);
  if (d.getSeconds() !== 0) return true;
  return !ON_GRID_MINUTES.includes(d.getMinutes());
}

export interface BookingHours {
  opening: number;
  closing: number;
  lunchStart: number;
  lunchEnd: number;
}

/** Reads reception opening/closing/lunch hours from org config, with sensible defaults. */
export function getBookingHours(config?: {
  booking_opening_hour?: number;
  booking_closing_hour?: number;
  booking_lunch_start?: number;
  booking_lunch_end?: number;
}): BookingHours {
  return {
    opening: config?.booking_opening_hour ?? 8,
    closing: config?.booking_closing_hour ?? 18,
    lunchStart: config?.booking_lunch_start ?? 12,
    lunchEnd: config?.booking_lunch_end ?? 13,
  };
}

/**
 * Closed (greyed-out) intervals within the visible grid: before opening, during
 * the lunch break, and after closing. Each is clamped to [GRID_HOUR_START, GRID_HOUR_END].
 */
export function getClosedIntervals(hours: BookingHours): [number, number][] {
  const intervals: [number, number][] = [];
  if (hours.opening > GRID_HOUR_START) intervals.push([GRID_HOUR_START, hours.opening]);
  if (hours.lunchEnd > hours.lunchStart) intervals.push([hours.lunchStart, hours.lunchEnd]);
  if (hours.closing < GRID_HOUR_END) intervals.push([hours.closing, GRID_HOUR_END]);
  return intervals;
}

/** Le créneau mord-il sur une période fermée (avant l'ouverture, pause déjeuner, après
 *  la fermeture) ? La grille va de 7h à 20h alors que le garage ouvre plus tard : on peut
 *  donc déposer un magnet là où ServiceBox refusera le RDV (« CCS indisponible » — le
 *  réceptionnaire n'est pas en poste). On laisse faire, mais on prévient. */
export function overlapsClosedInterval(
  startHour: number,
  durationHours: number,
  intervals: [number, number][],
): boolean {
  const end = startHour + Math.max(durationHours, 0);
  return intervals.some(([s, e]) => startHour < e - 1e-9 && s < end - 1e-9);
}

/** Deux gris distincts sur les plannings, pour lire d'un coup d'oeil pourquoi un créneau
 *  est indisponible :
 *  - la PAUSE (un trou au milieu de la journée : déjeuner, pause) : gris uni clair ;
 *  - le REPOS (avant l'ouverture / la prise de poste, après la fermeture, congé) :
 *    hachures plus foncées. */
export const PAUSE_BG = "rgba(120,120,120,0.13)";
/** Équivalent opaque de PAUSE_BG sur fond blanc (fond de cellule d'une zone « pause »). */
export const PAUSE_SOLID = "#EDEDED";
export const REPOS_BG =
  "repeating-linear-gradient(45deg, rgba(80,80,80,0.20), rgba(80,80,80,0.20) 6px, rgba(80,80,80,0.30) 6px, rgba(80,80,80,0.30) 12px)";

/** Un segment fermé (déjà découpé par closedSegmentsInHour) est une pause s'il vient
 *  d'une fermeture au milieu de la journée, un repos s'il touche le bord de la grille
 *  (avant l'ouverture, après la fermeture). */
export function closedSegmentBg(cs: number, ce: number, intervals: [number, number][]): string {
  const src = intervals.find(([s, e]) => cs >= s - 1e-9 && ce <= e + 1e-9);
  if (!src) return REPOS_BG;
  const midDay = src[0] > GRID_HOUR_START + 1e-9 && src[1] < GRID_HOUR_END - 1e-9;
  return midDay ? PAUSE_BG : REPOS_BG;
}

/** Même lecture pour un compagnon : un trou entre deux plages de travail est une pause,
 *  ce qui précède la prise de poste ou suit la fin de journée (ou une journée sans
 *  travail) est du repos. `working` = plages travaillées fusionnées et triées. */
export function offSegmentBg(os: number, oe: number, working: [number, number][]): string {
  if (!working.length) return REPOS_BG;
  const dayStart = working[0][0];
  const dayEnd = working[working.length - 1][1];
  return os >= dayStart - 1e-9 && oe <= dayEnd + 1e-9 ? PAUSE_BG : REPOS_BG;
}

/** Closed sub-segments clipped to a single hour cell [h, h+1] (empty when the cell is fully open). */
export function closedSegmentsInHour(h: number, intervals: [number, number][]): [number, number][] {
  const segs: [number, number][] = [];
  for (const [s, e] of intervals) {
    const start = Math.max(s, h);
    const end = Math.min(e, h + 1);
    if (end - start > 1e-9) segs.push([start, end]);
  }
  return segs;
}

/** Format a decimal hour as "8h00", "9h15", etc.
 *  Floors fractional minutes (7.5-min grid → 37.5 → 37) to match stored values. */
export function formatTime(h: number): string {
  const hours = Math.floor(h);
  const mins = Math.floor((h - hours) * 60 + 1e-9);
  return `${hours}h${mins.toString().padStart(2, "0")}`;
}

/** Convert decimal hour to HH:MM string (e.g. 8.25 → "08:15"). Floors fractional minutes. */
export function hourToTimeStr(h: number): string {
  const hours = Math.floor(h);
  const mins = Math.floor((h - hours) * 60 + 1e-9);
  return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
}

/** Convert HH:MM string to decimal hour (e.g. "08:15" → 8.25) */
export function timeStrToHour(s: string): number {
  const [h, m] = s.split(":").map(Number);
  return h + m / 60;
}

/** Format a date string (YYYY-MM-DD) + optional decimal hour as "12/04 à 9h30" */
export function formatDateHour(dateStr: string | null, hour?: number | null): string {
  if (!dateStr) return "—";
  const [, m, d] = dateStr.split("-");
  const datePart = `${d}/${m}`;
  if (hour == null) return datePart;
  const hrs = Math.floor(hour);
  const mins = Math.floor((hour - hrs) * 60 + 1e-9);
  return `${datePart} à ${hrs}h${mins.toString().padStart(2, "0")}`;
}

/** Position a fixed tooltip so it never overflows the viewport.
 *  Tries to place it to the right of the parent, falls back to left.
 */
export function positionTooltip(el: HTMLElement): void {
  const parent = el.parentElement;
  if (!parent) return;
  const rect = parent.getBoundingClientRect();
  const tw = el.offsetWidth;
  const th = el.offsetHeight;
  // Comfortable gap from every screen edge — the box must never hug a border.
  const M = 16;
  let top = rect.top;
  let left = rect.right + 8;
  if (left + tw > window.innerWidth - M) {
    left = rect.left - tw - 8;
  }
  if (left < M) left = M;
  if (top + th > window.innerHeight - M) {
    top = window.innerHeight - th - M;
  }
  if (top < M) top = M;
  el.style.top = `${top}px`;
  el.style.left = `${left}px`;
}
